import { useEffect, useMemo } from 'react'
import { format } from 'date-fns'
import { pl } from 'date-fns/locale'
import { useAppStore, type PresetKind } from '../store/appStore'
import {
  dayPhaseLabel,
  formatTime,
  getSunTimes,
  minutesOfDay,
  setMinutesOfDay,
  sunPosition,
} from '../lib/sun'

const PRESETS: { id: PresetKind; label: string }[] = [
  { id: 'sunrise', label: 'Wschód' },
  { id: 'goldenMorning', label: 'Golden AM' },
  { id: 'solarNoon', label: 'Południe' },
  { id: 'goldenEvening', label: 'Golden PM' },
  { id: 'sunset', label: 'Zachód' },
  { id: 'blueEvening', label: 'Blue hour' },
]

export function TimePanel() {
  const dateTime = useAppStore((s) => s.dateTime)
  const pin = useAppStore((s) => s.pin)
  const playing = useAppStore((s) => s.playing)
  const setDateTime = useAppStore((s) => s.setDateTime)
  const setPlaying = useAppStore((s) => s.setPlaying)
  const jumpToPreset = useAppStore((s) => s.jumpToPreset)

  const lat = pin?.lat ?? 51.1079
  const lng = pin?.lng ?? 17.0385
  const sun = useMemo(() => sunPosition(dateTime, lat, lng), [dateTime, lat, lng])
  const times = useMemo(() => getSunTimes(lat, lng, dateTime), [lat, lng, dateTime])
  const phase = dayPhaseLabel(dateTime, lat, lng)

  useEffect(() => {
    if (!playing) return
    const id = window.setInterval(() => {
      const cur = useAppStore.getState().dateTime
      const next = new Date(cur.getTime() + 60_000 * 3)
      const end = times.sunset ?? setMinutesOfDay(cur, 22 * 60)
      if (next > end) {
        useAppStore.getState().setPlaying(false)
        return
      }
      setDateTime(next)
    }, 50)
    return () => clearInterval(id)
  }, [playing, setDateTime, times.sunset])

  return (
    <section className="panel-glass rounded-2xl p-4 w-full max-w-xl">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p
            className="text-[11px] uppercase tracking-[0.18em] text-[var(--accent-2)]"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            Czas światła
          </p>
          <h2
            className="text-xl font-semibold leading-tight mt-1"
            style={{ fontFamily: 'var(--font-display)' }}
          >
            {format(dateTime, 'd MMMM yyyy', { locale: pl })}
          </h2>
          <p className="text-sm text-[var(--muted)]">{phase}</p>
        </div>
        <div className="text-right">
          <p className="text-2xl font-semibold tabular-nums" style={{ fontFamily: 'var(--font-display)' }}>
            {format(dateTime, 'HH:mm')}
          </p>
          <p className="text-sm font-semibold tabular-nums text-[var(--accent)] mt-0.5">
            az {sun.azimuthDeg.toFixed(0)}° · el {sun.altitudeDeg.toFixed(1)}°
          </p>
          <p className="text-[10px] text-[var(--muted)]">kąt padania światła</p>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-3">
        <input
          type="date"
          className="bg-black/30 border border-white/10 rounded-lg px-2 py-1.5 text-sm"
          value={format(dateTime, 'yyyy-MM-dd')}
          onChange={(e) => {
            const [y, m, d] = e.target.value.split('-').map(Number)
            const next = new Date(dateTime)
            next.setFullYear(y, m - 1, d)
            setDateTime(next)
          }}
        />
        <button
          type="button"
          className="px-3 py-1.5 rounded-lg text-sm bg-[var(--accent)] text-[#1a1200] font-medium"
          onClick={() => {
            setPlaying(false)
            setDateTime(new Date())
          }}
        >
          Teraz
        </button>
        <button
          type="button"
          className="px-3 py-1.5 rounded-lg text-sm border border-white/15 hover:bg-white/5"
          onClick={() => setPlaying(!playing)}
        >
          {playing ? 'Stop' : 'Przewiń dzień'}
        </button>
      </div>

      <input
        className="slider w-full"
        type="range"
        min={0}
        max={24 * 60 - 1}
        value={minutesOfDay(dateTime)}
        onChange={(e) => {
          setPlaying(false)
          setDateTime(setMinutesOfDay(dateTime, Number(e.target.value)))
        }}
      />

      <div className="mt-3 flex flex-wrap gap-1.5">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => jumpToPreset(p.id)}
            className="text-xs px-2.5 py-1 rounded-md border border-white/10 hover:border-[var(--accent)]/50 hover:bg-white/5"
          >
            {p.label}
          </button>
        ))}
      </div>

      <dl className="mt-3 grid grid-cols-3 gap-2 text-xs text-[var(--muted)]">
        <div>
          <dt>Wschód</dt>
          <dd className="text-[var(--ink)]">{formatTime(times.sunrise)}</dd>
        </div>
        <div>
          <dt>Południe</dt>
          <dd className="text-[var(--ink)]">{formatTime(times.solarNoon)}</dd>
        </div>
        <div>
          <dt>Zachód</dt>
          <dd className="text-[var(--ink)]">{formatTime(times.sunset)}</dd>
        </div>
        <div>
          <dt>Golden PM</dt>
          <dd className="text-[var(--ink)]">{formatTime(times.goldenHour)}</dd>
        </div>
        <div>
          <dt>Zmierzch cyw.</dt>
          <dd className="text-[var(--ink)]">{formatTime(times.dusk)}</dd>
        </div>
        <div>
          <dt>Noc</dt>
          <dd className="text-[var(--ink)]">{formatTime(times.night)}</dd>
        </div>
      </dl>
    </section>
  )
}
